import { useState, useEffect } from 'react';
import { supabase } from './supabase';
import { Truck, Plus, Edit2, Trash2, User, Loader, X, Save } from 'lucide-react';
import { useModal } from './AppModal';
import { useTheme } from './ThemeContext';

export default function VehiclesPage({ companyId }) {
  const { colors } = useTheme();
  const { showConfirm, showSuccess, showError } = useModal();

  const [loading, setLoading] = useState(true);
  const [vehicles, setVehicles] = useState([]);
  const [technicians, setTechnicians] = useState([]);

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [saving, setSaving] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    license_plate: '',
    vin: '',
    year: '',
    make: '',
    model: '',
    assigned_tech_id: '',
    notes: ''
  });

  const [isMobile, setIsMobile] = useState(window.innerWidth < 600);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 600);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (companyId) {
      loadVehicles();
      loadTechnicians();
    } else {
      setLoading(false);
    }
  }, [companyId]);

  const loadVehicles = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('vehicles')
        .select('*')
        .eq('company_id', companyId)
        .eq('is_active', true)
        .order('name');

      if (error) throw error;
      setVehicles(data || []);
    } catch (error) {
      console.error('Error loading vehicles:', error);
      showError('Failed to load vehicles');
    } finally {
      setLoading(false);
    }
  };

  const loadTechnicians = async () => {
    try {
      const { data: teamData } = await supabase
        .from('user_companies')
        .select('user_id, role')
        .eq('company_id', companyId);

      if (!teamData) return;

      const userIds = teamData.map(m => m.user_id);
      const { data: profiles } = await supabase
        .from('user_profiles')
        .select('id, full_name, email, role')
        .in('id', userIds);

      const techs = (teamData || [])
        .filter(m => m.role === 'owner' || m.role === 'field_tech')
        .map(m => {
          const profile = (profiles || []).find(p => p.id === m.user_id);
          return {
            id: m.user_id,
            name: profile?.full_name || profile?.email?.split('@')[0] || 'Unknown'
          };
        });

      setTechnicians(techs);
    } catch (error) {
      console.error('Error loading technicians:', error);
    }
  };

  const openCreateModal = () => {
    setEditingVehicle(null);
    setFormData({
      name: '',
      license_plate: '',
      vin: '',
      year: '',
      make: '',
      model: '',
      assigned_tech_id: '',
      notes: ''
    });
    setShowModal(true);
  };

  const openEditModal = (vehicle) => {
    setEditingVehicle(vehicle);
    setFormData({
      name: vehicle.name || '',
      license_plate: vehicle.license_plate || '',
      vin: vehicle.vin || '',
      year: vehicle.year || '',
      make: vehicle.make || '',
      model: vehicle.model || '',
      assigned_tech_id: vehicle.assigned_tech_id || '',
      notes: vehicle.notes || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (vehicle) => {
    const confirmed = await showConfirm(
      `Delete "${vehicle.name}"? This cannot be undone.`,
      { confirmStyle: 'danger' }
    );

    if (!confirmed) return;

    try {
      await supabase
        .from('vehicles')
        .update({ is_active: false })
        .eq('id', vehicle.id);

      showSuccess('Vehicle deleted');
      loadVehicles();
    } catch (error) {
      showError('Failed to delete vehicle');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      showError('Name is required');
      return;
    }

    setSaving(true);
    try {
      const vehicleData = {
        name: formData.name.trim(),
        license_plate: formData.license_plate.trim() || null,
        vin: formData.vin.trim() || null,
        year: formData.year ? parseInt(formData.year) : null,
        make: formData.make.trim() || null,
        model: formData.model.trim() || null,
        assigned_tech_id: formData.assigned_tech_id || null,
        notes: formData.notes.trim() || null
      };

      if (editingVehicle) {
        const { error } = await supabase
          .from('vehicles')
          .update({
            ...vehicleData,
            updated_at: new Date().toISOString()
          })
          .eq('id', editingVehicle.id);

        if (error) throw error;
        showSuccess('Vehicle updated');
      } else {
        const { error } = await supabase
          .from('vehicles')
          .insert({
            company_id: companyId,
            ...vehicleData
          });

        if (error) throw error;
        showSuccess('Vehicle added');
      }

      setShowModal(false);
      loadVehicles();
    } catch (error) {
      console.error('Error saving vehicle:', error);
      showError('Failed to save vehicle: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const getTechName = (techId) => {
    const tech = technicians.find(t => t.id === techId);
    return tech?.name || 'Unassigned';
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '50vh',
        color: colors.textMuted
      }}>
        <Loader size={32} style={{ animation: 'spin 1s linear infinite' }} />
        <span style={{ marginLeft: '1rem' }}>Loading vehicles...</span>
      </div>
    );
  }

  return (
    <div style={{ padding: isMobile ? '1rem' : '2rem' }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        justifyContent: 'space-between',
        alignItems: isMobile ? 'stretch' : 'center',
        gap: '1rem',
        marginBottom: '1.5rem'
      }}>
        <div>
          <h1 style={{ margin: 0, color: colors.text, fontSize: isMobile ? '1.5rem' : '2rem' }}>
            <Truck size={isMobile ? 24 : 32} style={{ marginRight: '0.75rem', verticalAlign: 'middle' }} />
            Vehicles
          </h1>
          <p style={{ margin: '0.5rem 0 0', color: colors.textMuted }}>
            Manage company vehicles
          </p>
        </div>
        <button
          onClick={openCreateModal}
          style={{
            padding: '0.75rem 1.5rem',
            background: colors.accent,
            border: 'none',
            borderRadius: '8px',
            color: '#fff',
            fontWeight: 'bold',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            justifyContent: 'center'
          }}
        >
          <Plus size={20} />
          Add Vehicle
        </button>
      </div>

      {/* Vehicles List */}
      {vehicles.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '3rem',
          color: colors.textMuted
        }}>
          <Truck size={48} style={{ marginBottom: '1rem', opacity: 0.5 }} />
          <p style={{ margin: 0 }}>No vehicles yet</p>
          <button
            onClick={openCreateModal}
            style={{
              marginTop: '1rem',
              padding: '0.75rem 1.5rem',
              background: colors.accent,
              border: 'none',
              borderRadius: '8px',
              color: '#fff',
              fontWeight: 'bold',
              cursor: 'pointer'
            }}
          >
            Add Your First Vehicle
          </button>
        </div>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(300px, 1fr))',
          gap: '1rem'
        }}>
          {vehicles.map(vehicle => (
            <div
              key={vehicle.id}
              style={{
                background: colors.bgCard,
                borderRadius: '12px',
                padding: '1.25rem',
                border: `1px solid ${colors.border}`
              }}
            >
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                marginBottom: '1rem'
              }}>
                <div>
                  <h3 style={{ margin: 0, color: colors.text, fontSize: '1.1rem' }}>
                    {vehicle.name}
                  </h3>
                  {vehicle.year && vehicle.make && vehicle.model && (
                    <p style={{ margin: '0.25rem 0 0', color: colors.textMuted, fontSize: '0.9rem' }}>
                      {vehicle.year} {vehicle.make} {vehicle.model}
                    </p>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button
                    onClick={() => openEditModal(vehicle)}
                    style={{
                      width: '36px',
                      height: '36px',
                      borderRadius: '8px',
                      background: colors.bgInput,
                      border: `1px solid ${colors.border}`,
                      color: colors.textMuted,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <Edit2 size={16} />
                  </button>
                  <button
                    onClick={() => handleDelete(vehicle)}
                    style={{
                      width: '36px',
                      height: '36px',
                      borderRadius: '8px',
                      background: colors.bgInput,
                      border: `1px solid ${colors.border}`,
                      color: colors.error,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>

              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '0.5rem',
                color: colors.textMuted,
                fontSize: '0.9rem'
              }}>
                {vehicle.license_plate && (
                  <div>License: {vehicle.license_plate}</div>
                )}
                {vehicle.assigned_tech_id && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <User size={14} />
                    {getTechName(vehicle.assigned_tech_id)}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.85)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '1rem'
          }}
          onClick={() => setShowModal(false)}
        >
          <div
            style={{
              background: colors.bgCard,
              borderRadius: '16px',
              width: '100%',
              maxWidth: '450px',
              maxHeight: '90vh',
              overflow: 'auto',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div style={{
              padding: '1.5rem',
              borderBottom: `1px solid ${colors.border}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <h2 style={{ margin: 0, color: colors.text, fontSize: '1.25rem' }}>
                {editingVehicle ? 'Edit Vehicle' : 'Add Vehicle'}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: colors.textMuted,
                  cursor: 'pointer'
                }}
              >
                <X size={24} />
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} style={{ padding: '1.5rem' }}>
              {/* Name */}
              <div style={{ marginBottom: '1.25rem' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  color: colors.text,
                  fontWeight: 'bold'
                }}>
                  Vehicle Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Service Van 1"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    background: colors.bgInput,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '8px',
                    color: colors.text
                  }}
                />
              </div>

              {/* Year, Make, Model */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr 1fr',
                gap: '0.75rem',
                marginBottom: '1.25rem'
              }}>
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    color: colors.textMuted,
                    fontSize: '0.9rem'
                  }}>Year</label>
                  <input
                    type="number"
                    value={formData.year}
                    onChange={(e) => setFormData({ ...formData, year: e.target.value })}
                    placeholder="2024"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      background: colors.bgInput,
                      border: `1px solid ${colors.border}`,
                      borderRadius: '8px',
                      color: colors.text
                    }}
                  />
                </div>
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    color: colors.textMuted,
                    fontSize: '0.9rem'
                  }}>Make</label>
                  <input
                    type="text"
                    value={formData.make}
                    onChange={(e) => setFormData({ ...formData, make: e.target.value })}
                    placeholder="Ford"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      background: colors.bgInput,
                      border: `1px solid ${colors.border}`,
                      borderRadius: '8px',
                      color: colors.text
                    }}
                  />
                </div>
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    color: colors.textMuted,
                    fontSize: '0.9rem'
                  }}>Model</label>
                  <input
                    type="text"
                    value={formData.model}
                    onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                    placeholder="Transit"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      background: colors.bgInput,
                      border: `1px solid ${colors.border}`,
                      borderRadius: '8px',
                      color: colors.text
                    }}
                  />
                </div>
              </div>

              {/* License Plate */}
              <div style={{ marginBottom: '1.25rem' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  color: colors.textMuted,
                  fontSize: '0.9rem'
                }}>License Plate</label>
                <input
                  type="text"
                  value={formData.license_plate}
                  onChange={(e) => setFormData({ ...formData, license_plate: e.target.value })}
                  placeholder="ABC-1234"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    background: colors.bgInput,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '8px',
                    color: colors.text
                  }}
                />
              </div>

              {/* Assigned Tech */}
              <div style={{ marginBottom: '1.25rem' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  color: colors.textMuted,
                  fontSize: '0.9rem'
                }}>Assigned Technician</label>
                <select
                  value={formData.assigned_tech_id}
                  onChange={(e) => setFormData({ ...formData, assigned_tech_id: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    background: colors.bgInput,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '8px',
                    color: colors.text
                  }}
                >
                  <option value="">Unassigned</option>
                  {technicians.map(tech => (
                    <option key={tech.id} value={tech.id}>{tech.name}</option>
                  ))}
                </select>
              </div>

              {/* Notes */}
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  color: colors.textMuted,
                  fontSize: '0.9rem'
                }}>Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Any additional notes..."
                  rows={2}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    background: colors.bgInput,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '8px',
                    color: colors.text,
                    resize: 'vertical'
                  }}
                />
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={saving}
                style={{
                  width: '100%',
                  padding: '1rem',
                  background: colors.accent,
                  border: 'none',
                  borderRadius: '8px',
                  color: '#fff',
                  fontWeight: 'bold',
                  cursor: saving ? 'not-allowed' : 'pointer',
                  opacity: saving ? 0.7 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem'
                }}
              >
                {saving ? (
                  <>
                    <Loader size={18} style={{ animation: 'spin 1s linear infinite' }} />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save size={18} />
                    {editingVehicle ? 'Update Vehicle' : 'Add Vehicle'}
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
